class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupLocaleSwitch();
    this.dispatchEvent(new CustomEvent('cartdrawer:constructed', { bubbles: true }));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Don't open mini bag if build kit confirmation modal is open
    const buildKitModal = document.getElementById('build-kit-add-to-bag-modal');
    if (buildKitModal && buildKitModal.open) {
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (!history.state || !history.state.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.checkForGiftCards();

    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.dispatchEvent(new CustomEvent('cartdrawer:open', { bubbles: true }));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    const drawerInner = this.querySelector('.drawer__inner');
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
     if (drawerInner.scrollHeight > viewportHeight) {
      this.querySelector('.drawer__footer-border-top').style.display = 'block';
     }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, fromKit, shouldOpen = true) {
    this.classList.contains('is-empty') &&
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (!fromKit) {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        if (shouldOpen) {
          this.open();
        }
      }
      this.checkForGiftCards();
    });
  }

  renderContentsFreeSamples(parsedState) {
    this.classList.contains('is-empty') &&
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.freeSamplesSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  freeSamplesSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
      {
        selector: 'drawer__footer'
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  checkForGiftCards() {
    if (this.isInternationalLocale()) {
      this.hideGiftCards();
    } else {
      this.restoreGiftCards();
    }
  }

  hideGiftCards() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const giftCardItems = cart.items.filter(item =>
          item.product_type === 'Gift Card' ||
          item.gift_card === true
        );

        if (giftCardItems.length > 0) {
          const storedGiftCards = giftCardItems.map(item => ({
            variant_id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
          }));

          localStorage.setItem('hiddenGiftCards', JSON.stringify(storedGiftCards));

          const updates = {};
          giftCardItems.forEach(item => {
            updates[item.variant_id] = 0;
          });

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: updates })
          })
          .then(response => response.json())
          .then(updatedCart => {
            const nonGiftCardItems = updatedCart.items.filter(item =>
              item.product_type !== 'Gift Card' && !item.gift_card
            );

            if (nonGiftCardItems.length === 0) {
              this.classList.add('is-empty');
              const cartDrawerItems = document.querySelector('cart-drawer-items');
              if (cartDrawerItems) {
                cartDrawerItems.classList.add('is-empty');
              }
            }
            this.refreshCartDrawer();
          });
        }
      })
      .catch(error => {
        console.error('Error hiding gift cards:', error);
      });
  }

  restoreGiftCards() {
    const storedGiftCards = localStorage.getItem('hiddenGiftCards');

    if (!storedGiftCards) {
      return;
    }

    try {
      const giftCardData = JSON.parse(storedGiftCards);
      this.bulkAddGiftCards(giftCardData);

    } catch (error) {
      console.error('Error parsing stored gift cards:', error);
      localStorage.removeItem('hiddenGiftCards');
    }
  }

  bulkAddGiftCards(giftCardData, callback) {
    const items = giftCardData.map(item => ({
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {}
    }));

    console.log('Bulk adding gift cards:', items);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: items
      })
    })
    .then(response => response.json())
    .then(addResult => {
      console.log('All gift cards restored successfully via bulk add:', addResult);
      localStorage.removeItem('hiddenGiftCards');

      if (addResult.items && addResult.items.length > 0) {
        this.classList.remove('is-empty');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.classList.remove('is-empty');
        }
      }

      if (callback) {
        callback();
        return Promise.resolve();
      }

      return fetch(`${window.routes.cart_url}?sections=cart-drawer,header`);
    })
    .then(response => {
      if (response && response.json) {
        return response.json();
      }
      return null;
    })
    .then(sections => {
      if (sections) {
        this.updateCartDrawerSections(sections);
      }
    })
    .catch(error => {
      console.error('Bulk add failed:', error);
      localStorage.removeItem('hiddenGiftCards');

      if (callback) {
        callback();
      } else {
        this.refreshCartDrawer();
      }
    });
  }

  updateCartDrawerSections(sections) {
    requestAnimationFrame(() => {
      if (sections['cart-drawer']) {
        const sectionDOM = this.getSectionDOM(sections['cart-drawer']);
        const cartDrawerElement = sectionDOM.querySelector('cart-drawer');
        const hasEmptyClass = cartDrawerElement && cartDrawerElement.classList.contains('is-empty');

        if (hasEmptyClass) {
          this.classList.add('is-empty');
          const existingCartDrawerItems = document.querySelector('cart-drawer-items');
          if (existingCartDrawerItems) {
            existingCartDrawerItems.classList.add('is-empty');
          }
        } else {
          this.classList.remove('is-empty');
          const existingCartDrawerItems = document.querySelector('cart-drawer-items');
          if (existingCartDrawerItems) {
            existingCartDrawerItems.classList.remove('is-empty');
          }
        }

        const drawerElement = document.querySelector('#CartDrawer');
        if (drawerElement) {
          drawerElement.innerHTML = cartDrawerElement ? cartDrawerElement.querySelector('#CartDrawer').innerHTML : '';
        }
      }

      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (cartIcon && sections['header']) {
        const headerDOM = this.getSectionDOM(sections['header']);
        const newCartIcon = headerDOM.querySelector('#cart-icon-bubble');
        if (newCartIcon) {
          cartIcon.innerHTML = newCartIcon.innerHTML;
        }
      }
    });
  }

  refreshCartDrawer() {
    fetch(`${window.routes.cart_url}?sections=cart-drawer,header`)
      .then(response => response.json())
      .then(sections => {
        this.updateCartDrawerSections(sections);
      })
      .catch(error => {
        console.error('Error refreshing cart drawer:', error);
        window.location.reload();
      });
  }

  isInternationalLocale() {
    const path = window.location.pathname;

    if (path === '/') return false;

    const localePattern = /^\/[a-z]{2}(-[a-z]{2})?(?:\/|$)/;
    return localePattern.test(path);
  }

  removeGiftCardsBeforeLocaleSwitch(localeLink) {
    const selectedLocale = localeLink.dataset.value;
    const usLocales = ['en', 'en-US', 'en-us', 'US'];
    const switchingToUS = usLocales.includes(selectedLocale);

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const giftCardItems = cart.items.filter(item =>
          item.product_type === 'Gift Card' ||
          item.gift_card === true
        );

        if (switchingToUS) {
          const storedGiftCards = localStorage.getItem('hiddenGiftCards');
          if (storedGiftCards) {
            console.log('Restoring gift cards for US locale');
            try {
              const giftCardData = JSON.parse(storedGiftCards);

              this.bulkAddGiftCards(giftCardData, () => {
                console.log('Gift cards restored, proceeding with locale switch');
                this.submitLocaleForm(localeLink);
              });
            } catch (error) {
              console.error('Error parsing stored gift cards:', error);
              localStorage.removeItem('hiddenGiftCards');
              this.submitLocaleForm(localeLink);
            }
          } else {
            this.submitLocaleForm(localeLink);
          }
        } else if (giftCardItems.length > 0) {
          const storedGiftCards = giftCardItems.map(item => ({
            variant_id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
          }));

          localStorage.setItem('hiddenGiftCards', JSON.stringify(storedGiftCards));

          const updates = {};
          giftCardItems.forEach(item => {
            updates[item.variant_id] = 0;
          });

          fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: updates })
          }).then(response => response.json())
            .then(() => {
              console.log('Gift cards stored and removed, proceeding with locale switch');
              this.submitLocaleForm(localeLink);
            })
            .catch(error => {
              console.error('Error removing gift cards:', error);
              this.submitLocaleForm(localeLink);
            });
        } else {
          this.submitLocaleForm(localeLink);
        }
      })
      .catch(error => {
        console.error('Error processing gift cards:', error);
        this.submitLocaleForm(localeLink);
      });
  }

  submitLocaleForm(localeLink) {
    const localizationForm = localeLink.closest('localization-form');
    const form = localizationForm.querySelector('form');
    const input = localizationForm.querySelector('input[name="locale_code"], input[name="country_code"]');

    if (form && input) {
      input.value = localeLink.dataset.value;
      form.submit();
    }
  }

  setupLocaleSwitch() {
    document.addEventListener('click', (event) => {
      const localeLink = event.target.closest('localization-form a[data-value]');
      if (localeLink) {
        event.preventDefault();
        this.removeGiftCardsBeforeLocaleSwitch(localeLink);
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const el = document.querySelector(section.selector);
      if (el && parsedState[section.section]) {
        el.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
      }
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.checkForGiftCards();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'header',
        selector: '#cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', () => {
  if (!window.cartDrawer) {
    window.cartDrawer = new CartDrawerItems();
  }

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.checkForGiftCards();
  }
});

if (!window.cartDrawer) {
  window.cartDrawer = new CartDrawerItems();
}

window.addEventListener('popstate', (event) => {
  const cartDrawerEl = document.querySelector('cart-drawer');
  if (cartDrawerEl) {
    if (cartDrawerEl?.classList.contains('active') && (!event.state || !event.state.cartDrawerOpen)) {
      cartDrawerEl.close();
    }
  }
});